import * as THREE from 'three';
import { createOrbMaterial } from '../fx/orbMaterial';
import { fxUniforms } from '../fx/common';
import { DirtyRange } from '../fx/common';
import { Runes } from '../fx/Runes';
import { KINDS, MAX_LEVEL, type Kind } from './config';

const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);
/** Orb radius at relic scale 1 and grade size 1 (m). */
export const ORB_RADIUS = 0.21;

/** Fixed-capacity instanced mesh with slot allocation. */
export class Pool {
  readonly mesh: THREE.InstancedMesh;
  private free: number[] = [];
  private top = 0;
  private readonly occupied: Uint8Array;
  private readonly matrices = new DirtyRange();
  private readonly colors = new DirtyRange();
  private readonly shading = new DirtyRange();
  private readonly shadingAttrs: THREE.InstancedBufferAttribute[];
  private readonly matrixAttrs: THREE.InstancedBufferAttribute[];
  private colorAttrs: THREE.InstancedBufferAttribute[] = [];
  constructor(geo: THREE.BufferGeometry, mat: THREE.Material, readonly cap: number, extra: Record<string, number> = {}) {
    this.occupied = new Uint8Array(cap);
    this.attrs = {};
    for (const [k, size] of Object.entries(extra)) {
      const a = new THREE.InstancedBufferAttribute(new Float32Array(cap * size), size).setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute(k, a); this.attrs[k] = a;
    }
    this.shadingAttrs = Object.values(this.attrs);
    this.mesh = new THREE.InstancedMesh(geo, mat, cap);
    this.mesh.count = 0;
    this.mesh.castShadow = true; this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.matrixAttrs = [this.mesh.instanceMatrix];
    for (let i = 0; i < cap; i++) this.mesh.setMatrixAt(i, ZERO);
  }
  readonly attrs: Record<string, THREE.InstancedBufferAttribute>;
  alloc(): number {
    const i = this.free.length ? this.free.pop()! : this.top;
    if (i >= this.cap) throw new Error('Pool capacity exceeded');
    this.top = Math.max(this.top, i + 1);
    this.occupied[i] = 1;
    this.mesh.count = Math.max(this.mesh.count, i + 1);
    return i;
  }
  release(i: number) {
    if (!this.occupied[i]) return;
    this.occupied[i] = 0;
    this.set(i, ZERO); this.free.push(i);
    while (this.mesh.count > 0 && !this.occupied[this.mesh.count - 1]) this.mesh.count--;
  }
  set(i: number, m: THREE.Matrix4) { this.mesh.setMatrixAt(i, m); this.matrices.mark(i); }
  color(i: number, c: THREE.Color) {
    const a = this.mesh.instanceColor;
    if (a && a.getX(i) === Math.fround(c.r) && a.getY(i) === Math.fround(c.g) && a.getZ(i) === Math.fround(c.b)) return;
    this.mesh.setColorAt(i, c); this.colors.mark(i);
    if (!this.colorAttrs.length) { this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage); this.colorAttrs.push(this.mesh.instanceColor!); }
  }
  markShading(i: number) { this.shading.mark(i); }
  flush() {
    this.mesh.visible = this.mesh.count > 0;
    this.matrices.flush(this.matrixAttrs);
    this.colors.flush(this.colorAttrs);
    this.shading.flush(this.shadingAttrs);
  }
}

/**
 * Every relic in the game, drawn with a fixed handful of instanced meshes:
 * stems, claws, one shared orb mesh, orbiting satellites and rune circles.
 */
export class RelicPool {
  readonly stems: Pool;
  readonly claws: Pool;
  readonly gems: Record<Kind, Pool[]>;
  readonly orbs: Pool;
  readonly shards: Pool;
  readonly bands: Pool;
  readonly rings: Pool;
  readonly runes = new Runes(96);

  constructor(scene: THREE.Scene, stemGeo: THREE.BufferGeometry, clawGeo: THREE.BufferGeometry, quality: 'low' | 'medium' | 'high' = 'high') {
    const metal = new THREE.MeshStandardMaterial({ name: 'relicMetal', color: 0xffffff, metalness: 0.95, roughness: 0.32 });
    this.stems = new Pool(stemGeo, metal, 64);
    this.claws = new Pool(clawGeo, metal, 64);
    // rank studs on the cup's lip: small faceted gold gems
    this.bands = new Pool(new THREE.OctahedronGeometry(0.032, 0).scale(1, 1.4, 1), metal, 320);
    this.bands.mesh.castShadow = false;
    this.stems.color(0, new THREE.Color()); this.claws.color(0, new THREE.Color());
    // every relic is now a glass orb; the element and grade live in the shader, so kinds and grades share a sphere
    const orbMat = createOrbMaterial({ volumeSteps: quality === 'low' ? 3 : 4 }), orbGeo = new THREE.IcosahedronGeometry(ORB_RADIUS, 4);
    this.orbs = new Pool(orbGeo, orbMat, 64, { iColor: 3, iGem: 4, iDetail: 2 });
    this.orbs.mesh.name = 'relicOrbs';
    this.gems = {} as Record<Kind, Pool[]>;
    for (const k of KINDS) {
      // Shape is identical; colour, rank and interior recipe are already per-instance attributes.
      this.gems[k] = Array.from({ length: MAX_LEVEL }, () => this.orbs);
    }
    // satellites: small wisp-orbs circling the artifact
    this.shards = new Pool(new THREE.IcosahedronGeometry(ORB_RADIUS * 0.28, 1), createOrbMaterial({ satellite: true }), 320, { iColor: 3, iGem: 4, iDetail: 2 });
    this.shards.mesh.name = 'relicMotes';
    this.shards.mesh.castShadow = false;
    // armillary rings: thin bronze hoops with runes that glow in the element's colour
    const ringMat = new THREE.MeshStandardMaterial({ name: 'armillary', color: 0xffffff, metalness: 0.9, roughness: 0.35 });
    ringMat.onBeforeCompile = (sh) => {
      sh.uniforms.uTime = fxUniforms.uTime;
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nattribute vec4 iRune; varying vec4 vRune; varying float vAng, vOut;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvRune = iRune; vAng = atan(position.y, position.x); vOut = dot(normalize(normal.xy), normalize(position.xy));');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', `#include <common>
          uniform float uTime; varying vec4 vRune; varying float vAng, vOut;
          float rh(float n) { return fract(sin(n * 91.7) * 43758.5453); }`)
        .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
          {
            // engraved glyphs on the outer face: short strokes in cells round the hoop, breathing slowly
            // sparse runes: a short mark in a third of the cells, only on the outer face
            float u = (vAng / 6.2832 + 0.5) * 24.0 + vRune.w * 13.0, cell = floor(u), f = fract(u);
            float glyph = step(0.66, rh(cell)) * step(abs(f - 0.5), 0.16) * smoothstep(0.55, 0.85, vOut);
            float pulse = 0.7 + 0.3 * sin(uTime * 2.0 + cell * 0.7);
            totalEmissiveRadiance += vRune.rgb * glyph * pulse;
          }`);
    };
    ringMat.customProgramCacheKey = () => 'armillary-runes';
    this.rings = new Pool(new THREE.TorusGeometry(1, 0.028, 5, 48), ringMat, 192, { iRune: 4 });
    this.rings.mesh.name = 'relicArmillary';
    this.rings.mesh.castShadow = false;
    scene.add(this.stems.mesh, this.claws.mesh, this.bands.mesh, this.shards.mesh, this.rings.mesh, this.runes.mesh);
    scene.add(this.orbs.mesh);
  }

  gem(kind: Kind, grade: number) { return this.gems[kind][grade - 1]; }

  /** Write per-instance gem shading params. */
  static shade(p: Pool, i: number, color: THREE.Color, glow: number, grade: number, flash: number, seed: number, element = 0, focus = 0) {
    p.attrs.iColor.setXYZ(i, color.r, color.g, color.b);
    p.attrs.iGem.setXYZW(i, glow, grade, flash, seed);
    p.attrs.iDetail.setXY(i, element, focus);
    p.markShading(i);
  }

  flush() {
    this.stems.flush(); this.claws.flush(); this.bands.flush(); this.shards.flush(); this.rings.flush(); this.runes.flush();
    this.orbs.flush();
  }
}
